/**
 * SAAS-PORTAL — Aprovação de Empresas (FASE 3, super_admin).
 *
 * Lista empresas PENDENTES (com contato do dono) para aprovar/rejeitar e as
 * empresas ativas/suspensas para suspender/reativar. Nunca exibe senha/hash.
 * Todas as ações chamam os endpoints platform-only e são auditadas no backend.
 */
import { useCallback, useEffect, useState } from "react";
import NavIcon from "../nav/NavIcon.jsx";
import { listPendingTenants, listTenants, tenantLifecycle } from "../lib/api.js";

const STATUS_LABEL = {
  pending: "Pendente",
  active: "Ativa",
  suspended: "Suspensa",
  rejected: "Rejeitada",
};

export default function TenantApprovalPage() {
  const [pending, setPending] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, t] = await Promise.all([listPendingTenants(), listTenants()]);
      setPending(p.tenants || []);
      setTenants(t.tenants || []);
    } catch (err) {
      setError(err?.message || "Falha ao carregar empresas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function act(tenantId, action, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    // SAAS-COMPANIES-UX-AUDIT-1 (quick win §9.5) — captura o MOTIVO em ações
    // sensíveis (suspender/rejeitar) e o envia p/ a auditoria. tenantLifecycle
    // já aceita {reason} e o backend já o grava (tenant.py _lifecycle_transition);
    // hoje a UI não passava e a razão se perdia na trilha. Cancelar o prompt
    // (null) aborta a ação; deixar em branco prossegue sem motivo.
    let reason;
    if (action === "suspend" || action === "reject") {
      const verbo = action === "suspend" ? "suspensão" : "rejeição";
      const input = window.prompt(
        `Motivo da ${verbo} (opcional, fica na auditoria):`, "");
      if (input === null) return; // cancelou
      reason = input.trim() || undefined;
    }
    setBusyId(tenantId + action);
    setError(null);
    try {
      await tenantLifecycle(tenantId, action, { reason });
      await load();
    } catch (err) {
      setError(err?.message || `Falha ao ${action}.`);
    } finally {
      setBusyId(null);
    }
  }

  const nonPending = tenants.filter((t) => t.status !== "pending");

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label mb-2">Governança SaaS</div>
          <h1 className="text-3xl font-semibold tracking-tight text-noc-text">Aprovação de empresas</h1>
          <p className="text-sm text-noc-muted mt-1">
            Revise novos cadastros e acompanhe o ciclo de vida das empresas.
          </p>
        </div>
        <button onClick={load} className="btn-ghost h-10 px-4 inline-flex items-center gap-2">
          <NavIcon name="refresh" size={15} />
          Atualizar
        </button>
      </header>

      {error && (
        <div className="flex items-center gap-2 text-sm text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg px-3 py-2">
          <span className="led-err" />{error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="stat-card">
          <div className="section-label">Pendentes</div>
          <div className="text-2xl font-semibold text-noc-accent mt-2">{pending.length}</div>
        </div>
        <div className="stat-card">
          <div className="section-label">Ativas</div>
          <div className="text-2xl font-semibold text-noc-ok mt-2">
            {nonPending.filter((t) => t.status === "active").length}
          </div>
        </div>
        <div className="stat-card">
          <div className="section-label">Suspensas</div>
          <div className="text-2xl font-semibold text-noc-warn mt-2">
            {nonPending.filter((t) => t.status === "suspended").length}
          </div>
        </div>
      </div>

      <section className="glass-card !p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-noc-divider flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-noc-text">Aguardando aprovação</h2>
            <p className="text-xs text-noc-muted mt-0.5">Cadastros que precisam de revisão da plataforma.</p>
          </div>
          {pending.length > 0 && (
            <span className="text-xs text-noc-accent bg-noc-accent/10 border border-noc-accent/25 rounded-full px-2.5 py-1">
              {pending.length} na fila
            </span>
          )}
        </div>
        <div className="divide-y divide-noc-divider">
          {loading && <div className="p-4 text-sm text-noc-muted">Carregando…</div>}
          {!loading && pending.length === 0 && (
            <div className="p-4 text-sm text-noc-muted">Nenhuma empresa pendente.</div>
          )}
          {pending.map((t) => (
            <div key={t.id} className="px-5 py-4 flex items-center gap-3 flex-wrap hover:bg-noc-panel2/30 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-noc-text truncate">{t.name}</div>
                <div className="text-[11px] text-noc-muted truncate">
                  {t.owner_full_name ? `${t.owner_full_name} · ` : ""}
                  <span className="font-mono">{t.owner_email || "—"}</span>
                  {t.contact_phone ? ` · ${t.contact_phone}` : ""}
                </div>
              </div>
              <button disabled={busyId === t.id + "approve"}
                onClick={() => act(t.id, "approve")}
                className="btn-primary h-8 px-3 text-xs">Aprovar</button>
              <button disabled={busyId === t.id + "reject"}
                onClick={() => act(t.id, "reject", `Rejeitar o cadastro de "${t.name}"?`)}
                className="btn-ghost h-8 px-3 text-xs">Rejeitar</button>
            </div>
          ))}
        </div>
      </section>

      <section className="glass-card !p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-noc-divider">
          <h2 className="text-sm font-semibold text-noc-text">Empresas cadastradas</h2>
          <p className="text-xs text-noc-muted mt-0.5">Status operacional e ações administrativas.</p>
        </div>
        <div className="divide-y divide-noc-divider">
          {!loading && nonPending.length === 0 && (
            <div className="p-4 text-sm text-noc-muted">Nenhuma empresa.</div>
          )}
          {nonPending.map((t) => (
            <div key={t.id} className="px-5 py-4 flex items-center gap-3 flex-wrap hover:bg-noc-panel2/30 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-noc-text truncate">{t.name}</div>
                <div className="text-[11px] text-noc-muted truncate font-mono">{t.slug}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${
                t.status === "active" ? "border-noc-ok/40 text-noc-ok"
                  : t.status === "suspended" ? "border-noc-warn/40 text-noc-warn"
                  : "border-noc-border text-noc-muted"}`}>
                {STATUS_LABEL[t.status] || t.status}
              </span>
              {t.status === "active" && (
                <button disabled={busyId === t.id + "suspend"}
                  onClick={() => act(t.id, "suspend", `Suspender "${t.name}"? Os usuários dessa empresa não poderão acessar.`)}
                  className="btn-ghost h-8 px-3 text-xs">Suspender</button>
              )}
              {t.status === "suspended" && (
                <button disabled={busyId === t.id + "reactivate"}
                  onClick={() => act(t.id, "reactivate")}
                  className="btn-primary h-8 px-3 text-xs">Reativar</button>
              )}
              {t.status === "rejected" && (
                <button disabled={busyId === t.id + "approve"}
                  onClick={() => act(t.id, "approve", `Aprovar "${t.name}" mesmo tendo sido rejeitada?`)}
                  className="btn-ghost h-8 px-3 text-xs">Aprovar</button>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
